import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaRocket, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaImage, FaTag, FaFont } from 'react-icons/fa';
import Layout from './Layout';
import SbGroupKeywordCampaignAPI from '../services/SbGroupKeywordCampaignAPI';
import '../assets/css/SbGroupKeywordCampaigns.css';

const SbGroupKeywordCampaigns = () => {
    // Files
    const [keywordsFile, setKeywordsFile] = useState(null);
    const [productsFile, setProductsFile] = useState(null);
    const [negativeFile, setNegativeFile] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [creativeFile, setCreativeFile] = useState(null);
    const [creativePreview, setCreativePreview] = useState(null);

    // Config
    const [headline, setHeadline] = useState('');
    const [dailyBudget, setDailyBudget] = useState(10);
    const [defaultBid, setDefaultBid] = useState(1.0);
    const [campaignState, setCampaignState] = useState('paused');
    const [brandEntityId, setBrandEntityId] = useState('');
    const [isManualBrandEntry, setIsManualBrandEntry] = useState(false);
    const [selectedCampaignIndices, setSelectedCampaignIndices] = useState(new Set());
    const [bulkAction, setBulkAction] = useState('');
    const [availableBrands, setAvailableBrands] = useState([]);
    const [adFormat, setAdFormat] = useState('productCollection');

    // Processed Data (for later steps)


    // UI State
    const [loading, setLoading] = useState(false);
    const [processingFiles, setProcessingFiles] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1); // 1: Setup, 2: Preview/Editor, 3: Execution Result

    const [generatedCampaigns, setGeneratedCampaigns] = useState([]);

    // -------------------------------------------------------------------------
    // Initial Data Fetching
    // -------------------------------------------------------------------------
    const getProfileId = () => {
        return localStorage.getItem('selectedAccountValue') || null;
    };



    // Better yet, just disable the warning for this line if fetchBrands isn't memoized
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchBrands();
    }, []);

    const fetchBrands = async () => {
        const profileId = getProfileId();
        if (profileId) {
            try {
                const response = await SbGroupKeywordCampaignAPI.getBrands(profileId);
                if (response.success && response.brands) {
                    setAvailableBrands(response.brands);
                    if (response.brands.length > 0 && !brandEntityId) {
                        setBrandEntityId(response.brands[0].brandEntityId);
                    }
                }
            } catch (err) {
                console.error("Error fetching brands:", err);
            }
        }
    };

    // -------------------------------------------------------------------------
    // File Handlers
    // -------------------------------------------------------------------------
    const handleKeywordsFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setKeywordsFile(file);
    };

    const handleProductsFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setProductsFile(file);
    };

    const handleNegativeFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setNegativeFile(file);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleCreativeChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCreativeFile(file);
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => setCreativePreview(reader.result);
                reader.readAsDataURL(file);
            } else if (file.type.startsWith('video/')) {
                setCreativePreview(URL.createObjectURL(file));
                setAdFormat('video');
            }
        }
    };

    // -------------------------------------------------------------------------
    // Logic: Process Files AND Generate Campaigns
    // -------------------------------------------------------------------------
    const handleGenerateClick = async () => {
        if (!brandEntityId) {
            setError('Please select a Brand Entity ID');
            return;
        }
        // New SB Collections format: required logo, optional creative, 3-10 ASINs
        if (!keywordsFile || !productsFile || !logoFile) {
            setError('Please upload required files: Keywords CSV, ASINs CSV, and logo.');
            return;
        }

        setProcessingFiles(true);
        setError(null);

        try {
            // 1. Upload & Parsing
            const response = await SbGroupKeywordCampaignAPI.uploadFiles(keywordsFile, productsFile, logoFile, creativeFile, negativeFile);

            if (response.success) {
                const loadedKeywords = response.data.keywords || [];
                const loadedProducts = response.data.products || []; // ASINs strings

                // Select all keywords by default
                // setSelectedKeywords(loadedKeywords);

                // New SB Collections format: 3-10 products (max 10)
                const initialSelectedProducts = loadedProducts.slice(0, 10);
                // setSelectedProducts(initialSelectedProducts);

                if (initialSelectedProducts.length < 3) {
                    setError(`Warning: Only ${initialSelectedProducts.length} ASINs were found. At least 3 ASINs are required.`);
                    // We let them proceed but it might warn
                }

                // 2. Generate Campaign Objects
                await generateCampaignsInternal(loadedKeywords, initialSelectedProducts, brandEntityId, headline);

                // 3. Move to Editor Step
                setStep(2);

            } else {
                setError(response.message || 'Error processing files');
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'Error uploading files');
        } finally {
            setProcessingFiles(false);
        }
    };

    const generateCampaignsInternal = async (kws, asins, brandId, headLineText) => {
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10);
        const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '');
        const randomSuffix = Math.floor(Math.random() * 1000);
        const baseName = `SB_Campaign_${dateStr}_${timeStr}_${randomSuffix}`;

        // Default Start Date: Tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const startDateStr = tomorrow.toISOString().slice(0, 10);

        const newCampaigns = kws.map((kw, index) => {
            const safeKeyword = kw.keywordText ? kw.keywordText.replace(/[^a-zA-Z0-9]/g, '_') : `kw${index}`;
            return {
                id: index,
                asins: asins,
                bid: kw.bid || defaultBid,
                brandEntityId: brandId,
                budget: dailyBudget,
                name: `${baseName}_${safeKeyword}`,
                costType: 'CPC',
                creativeName: creativeFile.name || 'Creative',
                goal: 'PAGE_VISIT',
                headline: headLineText || `Discover Our ${kw.keywordText || 'Products'} Collection`,
                keywordText: kw.keywordText,
                matchType: kw.matchType || 'broad',
                logoName: logoFile.name || 'Logo',
                startDate: startDateStr,
                smartDefault: 'TARGETING',
                portfolioId: '',
                status: 'READY'
            };
        });

        setGeneratedCampaigns(newCampaigns);
        setSelectedCampaignIndices(new Set());
    };

    // -------------------------------------------------------------------------
    // Actions in Editor (Step 2)
    // -------------------------------------------------------------------------

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIndices = new Set(generatedCampaigns.map((_, i) => i));
            setSelectedCampaignIndices(allIndices);
        } else {
            setSelectedCampaignIndices(new Set());
        }
    };

    const handleSelectRow = (index) => {
        const newSelection = new Set(selectedCampaignIndices);
        if (newSelection.has(index)) {
            newSelection.delete(index);
        } else {
            newSelection.add(index);
        }
        setSelectedCampaignIndices(newSelection);
    };

    const handleBulkActionApply = () => {
        if (selectedCampaignIndices.size === 0) {
            alert('Please select at least one campaign.');
            return;
        }

        const updated = [...generatedCampaigns];
        const indices = Array.from(selectedCampaignIndices);

        if (bulkAction === 'PAUSED') {
            indices.forEach(i => updated[i].status = "PAUSED");
        } else if (bulkAction === 'ENABLED') {
            indices.forEach(i => updated[i].status = "READY");
        } else if (bulkAction === 'SET_BUDGET') {
            const val = prompt('Enter the new budget:');
            if (val) indices.forEach(i => updated[i].budget = val);
        } else if (bulkAction === 'SET_START_DATE') {
            const val = prompt('Enter Start Date (YYYY-MM-DD):');
            if (val) indices.forEach(i => updated[i].startDate = val);
        } else if (bulkAction === 'SET_END_DATE') {
            const val = prompt('Enter End Date (YYYY-MM-DD):');
            if (val) indices.forEach(i => updated[i].endDate = val);
        }

        setGeneratedCampaigns(updated);
        setBulkAction('');
    };

    const handleExportCSV = () => {
        if (generatedCampaigns.length === 0) return;

        // Define headers
        const headers = [
            'Campaign Name', 'Brand Entity ID', 'Headline', 'ASINs', 'Budget', 'Bid',
            'Keyword', 'Match Type', 'Start Date', 'End Date', 'Portfolio ID',
            'Cost Type', 'Goal', 'Smart Default', 'Creative Path', 'Logo Path'
        ];

        // Map rows
        const rows = generatedCampaigns.map(c => [
            c.name, c.brandEntityId, c.headline, c.asins.join('|'), c.budget, c.bid,
            c.keywordText, c.matchType, c.startDate, c.endDate || '', c.portfolioId,
            c.costType, c.goal, c.smartDefault, c.creativeName, c.logoName
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `sb_campaigns_export_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const updateCampaignRow = (index, field, value) => {
        const updated = [...generatedCampaigns];
        updated[index][field] = value;
        setGeneratedCampaigns(updated);
    };

    const handleCreateAllCampaigns = async () => {
        const profileId = getProfileId();
        if (!profileId) {
            setError('No account selected.');
            return;
        }





        setLoading(true);
        setError(null);
        const results = [];

        for (let i = 0; i < generatedCampaigns.length; i++) {
            // If selection exists, only process selected
            if (selectedCampaignIndices.size > 0 && !selectedCampaignIndices.has(i)) {
                continue;
            }

            const camp = generatedCampaigns[i];

            const localCampaignState = camp.status === 'PAUSED' ? 'paused' : 'enabled';

            updateCampaignRow(i, 'status', 'CREATING');

            try {
                // Ensure endDate exists or calculate it
                let endDateStr = camp.endDate;
                if (!endDateStr) {
                    const start = new Date(camp.startDate);
                    const end = new Date(start);
                    end.setDate(end.getDate() + 30);
                    endDateStr = end.toISOString().slice(0, 10);
                }

                const createReqKey = {
                    profileId,
                    campaignName: camp.name,
                    dailyBudget: camp.budget,
                    headline: camp.headline,
                    keywords: [{
                        keywordText: camp.keywordText,
                        matchType: camp.matchType,
                        bid: camp.bid
                    }],
                    productAsins: camp.asins,
                    startDate: camp.startDate,
                    endDate: endDateStr,
                    defaultBid: camp.bid,
                    state: localCampaignState,
                    logo: logoFile,
                    creative: creativeFile,
                    brandEntityId: camp.brandEntityId,
                    adFormat
                };

                // Attempt 1
                let response = await SbGroupKeywordCampaignAPI.createCampaign(createReqKey);

                // Retry logic for Duplicate Name
                if (!response.success && response.message && response.message.includes('CAMPAIGN_NAME_ALREADY_EXISTS')) {
                    console.warn(`Campaign Name Collision for ${camp.name}. Retrying with suffix...`);
                    const safeSuffix = Math.floor(Math.random() * 10000);
                    const newName = `${camp.name}_${safeSuffix}`;

                    // Update the request and the local state
                    createReqKey.campaignName = newName;
                    updateCampaignRow(i, 'name', newName);

                    response = await SbGroupKeywordCampaignAPI.createCampaign(createReqKey);
                }

                if (response.success) {
                    updateCampaignRow(i, 'status', 'SUCCESS');
                    results.push({ name: createReqKey.campaignName, success: true, id: response.data.campaign.campaignId });
                } else {
                    updateCampaignRow(i, 'status', 'ERROR');
                    results.push({ name: camp.name, success: false, message: response.message });
                }

            } catch (err) {
                console.error(err);
                updateCampaignRow(i, 'status', 'ERROR');
                results.push({ name: camp.name, success: false, message: err.message });
            }
        }
        setResult({ success: true, results });
        setLoading(false);
        setStep(3); // Go to results
    };

    // Status Badge Helpers
    const StatusBadge = ({ status }) => {
        if (status === 'SUCCESS') return <Badge bg="success"><FaCheckCircle /></Badge>;
        if (status === 'ERROR') return <Badge bg="danger"><FaExclamationTriangle /></Badge>;
        if (status === 'CREATING') return <Spinner size="sm" animation="border" />;
        if (status === 'PAUSED') return <Badge bg="warning" text="dark">PAUSED</Badge>;
        return <Badge bg="secondary">READY</Badge>;
    };

    // -------------------------------------------------------------------------
    // RENDER
    // -------------------------------------------------------------------------
    return (
        <Layout>
            <Container fluid className="sb-keyword-campaigns-page py-4">

                {/* Header */}
                <Row className="mb-4">
                    <Col className="page-hero">
                        <h2>Sponsored Brands Campaigns</h2>
                        <p className="page-subtitle">Create Sponsored Brands campaigns with product collections</p>
                    </Col>
                </Row>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        <FaExclamationTriangle className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* Step 1: Setup View */}
                {step === 1 && (
                    <>
                        {/* Config Section */}
                        <div className="config-section-card">
                            <Row>
                                <Col md={12} className="mb-3">
                                    <div className="field-clarity required-field">
                                    <label className="form-label-strong mb-2 fw-bold d-flex align-items-center gap-2">
                                        <FaTag className="config-label-icon" /> Brand Entity ID
                                        <span className="field-badge required-badge">Required at creation</span>
                                    </label>
                                    <Form.Select
                                        className="custom-select" value={isManualBrandEntry ? 'MANUAL' : brandEntityId}
                                        onChange={e => {
                                            const val = e.target.value;
                                            if (val === 'MANUAL') {
                                                setIsManualBrandEntry(true);
                                                setBrandEntityId('');
                                            } else {
                                                setIsManualBrandEntry(false);
                                                setBrandEntityId(val);
                                            }
                                        }}
                                    >
                                        <option value="">Select a Brand Entity ID or choose manual entry...</option>
                                        {availableBrands.map((b, i) => (
                                            /* Changed display to show ID as primary, per user request */
                                            <option key={i} value={b.brandEntityId}>{b.brandEntityId} ({b.brandRegistryName || 'Unknown'})</option>
                                        ))}
                                        <option value="MANUAL">Enter manually...</option>
                                    </Form.Select>

                                    {isManualBrandEntry && (
                                        <div className="mt-2">
                                            <Form.Control
                                                type="text"
                                                className="custom-input"
                                                placeholder="Example: ENTITY36T8RSV4AA8U7"
                                                value={brandEntityId}
                                                onChange={e => setBrandEntityId(e.target.value)}
                                            />
                                        </div>
                                    )}

                                    <div className="input-helper-text helper-emphasis">
                                        Provide it here, or include it in the ASIN CSV column "Brand Entity ID". If missing in both places, campaign creation will fail.
                                    </div>
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <div className="field-clarity optional-field">
                                    <label className="form-label-strong mb-2 fw-bold d-flex align-items-center gap-2">
                                        <FaFont className="config-label-icon" /> Headline
                                        <span className="field-badge optional-badge">Optional</span>
                                    </label>
                                    <Form.Control
                                        type="text"
                                        className="custom-input" placeholder="Example: Discover our best-selling collection"
                                        value={headline}
                                        onChange={e => setHeadline(e.target.value)}
                                    />
                                    <div className="input-helper-text helper-emphasis">
                                        If left empty, the system uses: "Discover Our [Keyword] Collection".
                                    </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Upload Grid */}
                        <div className="upload-grid">

                            {/* Card 1: Keywords */}
                            <div className={`upload-card-new ${keywordsFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Keywords File</div>
                                <div className="upload-card-desc">
                                    CSV file with columns: 'Keyword', 'Match Type', 'Bid'
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {keywordsFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleKeywordsFileChange} hidden />
                                </label>
                                {keywordsFile && <small className="text-success mt-2">{keywordsFile.name}</small>}
                            </div>

                            {/* Card 2: ASINs */}
                            <div className={`upload-card-new ${productsFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">ASINs File</div>
                                <div className="upload-card-desc">
                                    CSV file with at least 3 ASINs
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {productsFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleProductsFileChange} hidden />
                                </label>
                                {productsFile && <small className="text-success mt-2">{productsFile.name}</small>}
                            </div>

                            {/* Card 3: Logo */}
                            <div className={`upload-card-new ${logoFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon">
                                    {logoPreview ? <img src={logoPreview} alt="Logo" style={{ maxHeight : '50px' }} /> : <FaImage />}
                                </div>
                                <div className="upload-card-title">Brand Logo</div>
                                <div className="upload-card-desc">
                                    JPG or PNG, recommended size 400x400px
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {logoFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept="image/*" onChange={handleLogoChange} hidden />
                                </label>
                                {logoFile && <small className="text-success mt-2">{logoFile.name}</small>}
                            </div>

                            {/* Card 4: Creative (Optional for SB Collections) */}
                            <div className={`upload-card-new ${creativeFile ? 'has-file' : ''}`}>
                                <div className="upload-icon">
                                    {creativePreview ? (creativeFile.type.includes('video') ? <FaFileAlt /> : <img src={creativePreview} alt="Creative" style={{ maxHeight: '50px' }} />) : <FaImage />}
                                </div>
                                <div className="upload-card-title">Creative (Optional)</div>
                                <div className="upload-card-desc">
                                    JPG/PNG image or MP4/MOV video. Amazon can generate assets automatically.
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {creativeFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept="image/*,video/*" onChange={handleCreativeChange} hidden />
                                </label>
                                {creativeFile && <small className="text-success mt-2">{creativeFile.name}</small>}
                            </div>

                            {/* Card 5: Negative Targeting */}
                            <div className={`upload-card-new ${negativeFile ? 'has-file' : ''}`}>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Negative Targeting (Optional)</div>
                                <div className="upload-card-desc">
                                    Columns: Abbreviation, Negative Targeting, Match Type
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {negativeFile ? 'Change File' : 'Select File'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleNegativeFileChange} hidden />
                                </label>
                                {negativeFile && <small className="text-success mt-2">{negativeFile.name}</small>}
                            </div>

                        </div>

                        {/* Generate Button */}
                        <div className="generate-section">
                            <button className={`btn-generate-campaigns ${(!processingFiles && !loading) ? 'ready' : ''}`}
                                onClick={handleGenerateClick}
                                disabled={processingFiles || loading}
                            >
                                {processingFiles ? (
                                    <><Spinner size="sm" animation="border" className="me-2" /> Processing...</>
                                ) : (
                                    <><FaRocket /> Generate Sponsored Brands Campaigns</>
                                )}
                            </button>
                        </div>

                        {/* Requirements Footer - New SB Collections Format 2026 */}
                        <div className="requirements-section">
                            <span className="requirements-title">File Requirements (New SB Collections Format)</span>
                            <div className="req-item"><strong>Keywords CSV:</strong> Columns: 'Keyword', 'Match Type', 'Bid'. Optional: Abbreviation.</div>
                            <div className="req-item"><strong>ASINs CSV:</strong> Columns: 'ASIN' or 'ASINs'. <strong>Requires 3-10 ASINs</strong>.</div>
                            <div className="req-item"><strong>Logo:</strong> JPG or PNG image, recommended size 400x400px. <strong>Required</strong>.</div>
                            <div className="req-item"><strong>Creative:</strong> Image or video. <strong>Optional</strong> - Amazon AI can generate it automatically.</div>
                            <div className="req-item"><strong>Headline:</strong> <strong>Optional</strong> - Amazon AI can generate it automatically.</div>
                            <div className="req-item"><strong>Negative Targeting CSV:</strong> Columns: 'Abbreviation', 'Negative Targeting', 'Match Type'. (Optional)</div>
                            <div className="req-item"><strong>Brand Entity ID:</strong> Required to create Sponsored Brands campaigns.</div>
                        </div>
                    </>
                )}

                {/* Step 2: Editor (Formerly Step 3) */}
                {step === 2 && (
                    <Row>
                        <Col md={12}>
                            <Card className="config-section-card border-0 p-0 overflow-hidden">
                                <Card.Header className="bg-dark text-white p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5><FaRocket className="me-2" /> Campaign Editor ({generatedCampaigns.length})</h5>
                                        <Button variant="warning" className="fw-bold text-dark">
                                            Select Portfolios
                                        </Button>
                                    </div>

                                    {/* Bulk Actions Bar */}
                                    <div className="d-flex align-items-center gap-2">
                                        <strong className="text-white me-2">Bulk actions:</strong>
                                        <Form.Select
                                            size="sm" style={{ width : '200px', backgroundColor: '#fff', color: '#000' }}
                                            value={bulkAction}
                                            onChange={e => setBulkAction(e.target.value)}
                                        >
                                            <option value="">Select action</option>
                                            <option value="PAUSED">Change to Paused</option>
                                            <option value="ENABLED">Change to Enabled</option>
                                            <option value="SET_BUDGET">Set budget...</option>
                                            <option value="SET_START_DATE">Set start date...</option>
                                            <option value="SET_END_DATE">Set end date...</option>
                                        </Form.Select>
                                        <Button variant="warning" size="sm" onClick={handleBulkActionApply} className="text-dark fw-bold">
                                            Apply
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0 table-responsive bg-dark" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <Table hover variant="dark" className="align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                                        <thead className="text-warning sticky-top bg-dark">
                                            <tr>
                                                <th width="40"><Form.Check type="checkbox" onChange={handleSelectAll} /></th>
                                                <th>ASINs</th>
                                                <th width="70">Bid</th>
                                                <th width="150">Brand Entity ID</th>
                                                <th width="80">Budget</th>
                                                <th width="200">Campaign Name</th>
                                                <th>Cost Type</th>
                                                <th>Goal</th>
                                                <th width="200">Headline</th>
                                                <th>Keyword</th>
                                                <th>Creative Path</th>
                                                <th>Logo Path</th>
                                                <th>Match Type</th>
                                                <th>Portfolio ID</th>
                                                <th>Smart Default</th>
                                                <th width="110">Start Date</th>
                                                <th>State</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {generatedCampaigns.map((camp, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selectedCampaignIndices.has(idx)}
                                                            onChange={() => handleSelectRow(idx)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '80px' }} title={camp.asins.join(', ')}>
                                                            {camp.asins.length} ASINs
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Form.Control type="number" size="sm" value={camp.bid} onChange={e => updateCampaignRow(idx, 'bid', e.target.value)} className="form-control-dark p-0 px-1" />
                                                    </td>
                                                    <td>
                                                        <Form.Control type="text" size="sm" value={camp.brandEntityId} readOnly className="form-control-dark p-0 px-1" />
                                                    </td>
                                                    <td>
                                                        <Form.Control type="number" size="sm" value={camp.budget} onChange={e => updateCampaignRow(idx, 'budget', e.target.value)} className="form-control-dark p-0 px-1" />
                                                    </td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                                            <Form.Control type="text" size="sm" value={camp.name} onChange={e => updateCampaignRow(idx, 'name', e.target.value)} className="form-control-dark p-0 px-1" />
                                                        </div>
                                                    </td>
                                                    <td>{camp.costType}</td>
                                                    <td>{camp.goal}</td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                                            <Form.Control type="text" size="sm" value={camp.headline} onChange={e => updateCampaignRow(idx, 'headline', e.target.value)} maxLength={50} className="form-control-dark p-0 px-1" />
                                                        </div>
                                                    </td>
                                                    <td className="text-info">{camp.keywordText}</td>
                                                    <td><div className="text-truncate" style={{ maxWidth: '100px' }} title={camp.creativeName}>{camp.creativeName}</div></td>
                                                    <td><div className="text-truncate" style={{ maxWidth: '80px' }} title={camp.logoName}>{camp.logoName}</div></td>
                                                    <td><Badge bg="secondary">{camp.matchType}</Badge></td>
                                                    <td>
                                                        <Form.Control type="text" size="sm" value={camp.portfolioId || ''} onChange={e => updateCampaignRow(idx, 'portfolioId', e.target.value)} className="form-control-dark p-0 px-1" />
                                                    </td>
                                                    <td>{camp.smartDefault}</td>
                                                    <td>
                                                        <Form.Control type="date" size="sm" value={camp.startDate} onChange={e => updateCampaignRow(idx, 'startDate', e.target.value)} className="form-control-dark p-0 px-1" />
                                                    </td>
                                                    <td><StatusBadge status={camp.status} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                                    <div className="d-flex gap-2">
                                        <Button variant="secondary" onClick={() => setStep(1)} className="fw-bold">
                                            Back
                                        </Button>
                                        <Button variant="warning" onClick={handleExportCSV} className="fw-bold text-dark">
                                            Export CSV
                                        </Button>
                                    </div>
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="text-muted small">
                                            Total: {generatedCampaigns.length}
                                        </div>
                                        <Button variant="success" onClick={handleCreateAllCampaigns} disabled={loading} className="fw-bold">
                                            {loading ? <Spinner animation="border" size="sm" /> : (selectedCampaignIndices.size > 0 ? 'Create Selected Campaigns' : 'Create All Campaigns')}
                                        </Button>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Step 3: Result */}
                {step === 3 && result && (
                    <Row>
                        <Col md={10} className="mx-auto">
                            <Card className="bg-dark text-white border-0">
                                <Card.Header className="bg-success text-white">
                                    <FaCheckCircle className="me-2" /> Execution Complete
                                </Card.Header>
                                <Card.Body>
                                    <Table variant="dark" striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Campaign</th>
                                                <th>Status</th>
                                                <th>Details</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.results && result.results.map((res, i) => (
                                                <tr key={i}>
                                                    <td>{res.name}</td>
                                                    <td>{res.success ? <Badge bg="success">Created</Badge> : <Badge bg="danger">Failed</Badge>}</td>
                                                    <td>
                                                        {res.success ? (
                                                            <small className="text-muted">ID: {res.id}</small>
                                                        ) : (
                                                            <small className="text-danger">{res.message}</small>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <div className="d-flex justify-content-end mt-3">
                                        <Button variant="primary" onClick={() => window.location.reload()}>Create More</Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

            </Container>
        </Layout>
    );
};

export default SbGroupKeywordCampaigns;

